import { useState, useCallback, useMemo } from "react";
import { useRole } from "@/data/RoleContext";
import { usePermissions } from "@/data/PermissionsContext";
import { PermissionGate } from "@/components/PermissionGate";
import { useNavigationConfig, NavCategoryConfig, NavItemConfig, CustomShortcutGroup, NavigationConfig } from "@/data/NavigationConfigContext";
import { useNavMode } from "@/data/NavModeContext";
import { useUISkin } from "@/data/UISkinContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  GripVertical, Eye, EyeOff, Plus, Trash2,
  Save, ChevronUp, ChevronDown, FolderPlus, Link2, Shield, Info,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

/* ═══════════════════════════════════════════
   DEFAULT NAV STRUCTURE — extracted from AppLayout's super_admin
   ═══════════════════════════════════════════ */

const DEFAULT_CATEGORIES: NavCategoryConfig[] = [
  {
    id: "Leadership", label: "Leadership", visible: true,
    items: [
      { label: "Command Dashboard", path: "/command-dashboard", visible: true },
      { label: "Appointment Results", path: "/appointment-results", visible: true },
      { label: "Rep Scorecards", path: "/rep-scorecards", visible: true },
    ],
  },
  {
    id: "Leads Config", label: "Leads Config", visible: true,
    items: [
      { label: "Lead Sources", path: "/lead-sources", visible: true },
      { label: "Lead Integrity", path: "/lead-integrity", visible: true },
    ],
  },
  {
    id: "Leads Management", label: "Leads Management", visible: true,
    items: [
      { label: "Leads", path: "/pipeline", visible: true },
      { label: "Conversations", path: "/messages", visible: true },
      { label: "Follow-Ups", path: "/follow-ups", visible: true },
      { label: "Appointments", path: "/appointments", visible: true },
    ],
  },
  {
    id: "Sales", label: "Sales", visible: true,
    items: [
      { label: "Opportunities", path: "/opportunities", visible: true },
    ],
  },
  {
    id: "Pre-Construction", label: "Pre-Construction", visible: true,
    items: [
      { label: "Build Review", path: "/intake-review", visible: true },
      { label: "Permit Tracker", path: "/permitting", visible: true },
    ],
  },
  {
    id: "Materials & Procurement", label: "Materials & Procurement", visible: true,
    items: [
      { label: "Ordering Queue", path: "/ordering-queue", visible: true },
      { label: "Vendors", path: "/vendors", visible: true },
      { label: "Inventory", path: "/inventory", visible: true },
      { label: "Warehouse Hub", path: "/warehouse", visible: true },
    ],
  },
  {
    id: "Operations", label: "Operations", visible: true,
    items: [
      { label: "Command Center", path: "/", visible: true },
      { label: "Jobs", path: "/jobs", visible: true },
      { label: "Customers", path: "/customers", visible: true },
    ],
  },
  {
    id: "Execution", label: "Execution", visible: true,
    items: [
      { label: "Scheduling", path: "/scheduling", visible: true },
      { label: "Dispatch Board", path: "/scheduling-dispatch", visible: true },
      { label: "Dispatch Intelligence", path: "/dispatch-intelligence", visible: true },
      { label: "Install Readiness", path: "/install-readiness", visible: true },
      { label: "Installer Dashboard", path: "/installer-dashboard", visible: true },
    ],
  },
  {
    id: "Quality & Service", label: "Quality & Service", visible: true,
    items: [
      { label: "Install QA", path: "/qa-form", visible: true },
      { label: "Issues", path: "/issues", visible: true },
      { label: "Service Tickets", path: "/service-tickets", visible: true },
      { label: "Report Issue", path: "/issue-form", visible: true },
    ],
  },
  {
    id: "Financials", label: "Financials", visible: true,
    items: [
      { label: "Accounting", path: "/accounting", visible: true },
      { label: "Revenue Intelligence", path: "/revenue-intelligence", visible: true },
      { label: "Earnings Intelligence", path: "/earnings-intelligence", visible: true },
      { label: "Commissions", path: "/commissions", visible: true },
    ],
  },
  {
    id: "Automation & Intelligence", label: "Automation & Intelligence", visible: true,
    items: [
      { label: "Pipeline Intelligence", path: "/pipeline-intelligence", visible: true },
      { label: "Automations", path: "/automations", visible: true },
      { label: "Follow-Up Engine", path: "/follow-up-engine", visible: true },
    ],
  },
  {
    id: "Reporting", label: "Reporting", visible: true,
    items: [
      { label: "Reports", path: "/reports", visible: true },
      { label: "Marketing Attribution", path: "/marketing-attribution", visible: true },
      { label: "Disposition Report", path: "/disposition-report", visible: true },
      { label: "Alerts History", path: "/alerts", visible: true },
      { label: "System Audit", path: "/system-audit", visible: true },
      { label: "Architecture Health", path: "/architecture-health", visible: true },
      { label: "Intelligence", path: "/intelligence", visible: true },
    ],
  },
  {
    id: "Contractor Network", label: "Contractor Network", visible: true,
    items: [
      { label: "Intake Pipeline", path: "/contractor-intake", visible: true },
      { label: "Onboarding", path: "/contractor-onboarding", visible: true },
      { label: "Directory", path: "/contractors", visible: true },
    ],
  },
  {
    id: "Portals", label: "Portals", visible: true,
    items: [
      { label: "Portal Hub", path: "/portals", visible: true },
    ],
  },
  {
    id: "Catalog", label: "Catalog", visible: true,
    items: [
      { label: "Offerings", path: "/offerings", visible: true },
      { label: "Product Registry", path: "/product-registry", visible: true },
    ],
  },
];

/* ═══════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════ */

export default function NavigationSettings() {
  const { role } = useRole();
  const { hasPermission: hasPerm } = usePermissions();
  const { config, setConfig } = useNavigationConfig();
  const { navMode, setNavMode } = useNavMode();
  const { skin, setSkin } = useUISkin();

  // Working state — start from config or defaults
  const [categories, setCategories] = useState<NavCategoryConfig[]>(
    config?.categories ?? JSON.parse(JSON.stringify(DEFAULT_CATEGORIES))
  );
  const [customGroups, setCustomGroups] = useState<CustomShortcutGroup[]>(
    config?.customGroups ?? []
  );
  const [dirty, setDirty] = useState(false);
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [selectedPaths, setSelectedPaths] = useState<string[]>([]);

  // All available modules for shortcut group picker
  const allModules = useMemo(() => {
    const modules: { label: string; path: string; category: string }[] = [];
    for (const cat of DEFAULT_CATEGORIES) {
      for (const item of cat.items) {
        modules.push({ label: item.label, path: item.path, category: cat.label });
      }
    }
    return modules;
  }, []);

  const moveCategory = useCallback((idx: number, dir: -1 | 1) => {
    setCategories((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
    setDirty(true);
  }, []);

  const toggleCategoryVisibility = useCallback((idx: number) => {
    setCategories((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], visible: !next[idx].visible };
      return next;
    });
    setDirty(true);
  }, []);

  const moveItem = useCallback((catIdx: number, itemIdx: number, dir: -1 | 1) => {
    setCategories((prev) => {
      const next = [...prev];
      const items = [...next[catIdx].items];
      const target = itemIdx + dir;
      if (target < 0 || target >= items.length) return prev;
      [items[itemIdx], items[target]] = [items[target], items[itemIdx]];
      next[catIdx] = { ...next[catIdx], items };
      return next;
    });
    setDirty(true);
  }, []);

  const toggleItemVisibility = useCallback((catIdx: number, itemIdx: number) => {
    setCategories((prev) => {
      const next = [...prev];
      const items = [...next[catIdx].items];
      items[itemIdx] = { ...items[itemIdx], visible: !items[itemIdx].visible };
      next[catIdx] = { ...next[catIdx], items };
      return next;
    });
    setDirty(true);
  }, []);

  const handleSave = () => {
    if (!dirty) {
      toast.success(
        skin === "cockpit"
          ? "Koredor visual language is already saved"
          : "No navigation layout changes to save",
        {
          description: "Visual language saves instantly when selected.",
        }
      );
      return;
    }

    const navConfig: NavigationConfig = { categories, customGroups };
    setConfig(navConfig);
    setDirty(false);
    toast.success("Navigation layout saved");
  };

  const handleCreateGroup = () => {
    if (!newGroupName.trim() || selectedPaths.length === 0) return;
    const group: CustomShortcutGroup = {
      id: `custom-${Date.now()}`,
      label: newGroupName.trim(),
      itemPaths: selectedPaths,
    };
    setCustomGroups((prev) => [...prev, group]);
    setDirty(true);
    setShowGroupDialog(false);
    setNewGroupName("");
    setSelectedPaths([]);
    toast.success(`Shortcut group "${group.label}" created`);
  };

  const removeGroup = (id: string) => {
    setCustomGroups((prev) => prev.filter((g) => g.id !== id));
    setDirty(true);
  };

  // Access control
  const canEdit = hasPerm("settings_edit");

  if (!canEdit) {
    return (
      <div className="max-w-3xl mx-auto py-10 px-4">
        <Card>
          <CardContent className="py-12 text-center">
            <Shield className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">Navigation configuration is only available to Admin and Owner roles.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Navigation Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Customize sidebar layout, reorder categories, and create shortcut groups.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PermissionGate permission="settings_edit">
            <Button size="sm" onClick={handleSave}>
              <Save className="h-3.5 w-3.5 mr-1.5" />
              Save Layout
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="py-3 px-4 flex items-start gap-3">
          <Info className="h-4 w-4 text-primary mt-0.5 shrink-0" />
          <div className="text-xs text-muted-foreground">
            <strong className="text-foreground">UI-only changes.</strong> Reordering or hiding categories changes the sidebar layout only.
            All modules and routes remain fully accessible. Hidden items can be reached via direct URL.
          </div>
        </CardContent>
      </Card>

      {/* Navigation Mode Toggle */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Navigation Mode</CardTitle>
          <CardDescription className="text-xs">
            Switch between the full Standard sidebar and the curated, action-first Command Rail. Changes apply instantly with no reload.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setNavMode("standard")}
              className={cn(
                "text-left rounded-lg border p-3 transition-all",
                navMode === "standard"
                  ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                  : "border-border hover:border-foreground/30"
              )}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold">Standard Sidebar</span>
                {navMode === "standard" && <Badge variant="secondary" className="text-[10px]">Active</Badge>}
              </div>
              <p className="text-xs text-muted-foreground">
                Full grouped navigation with every category visible. Best for power users.
              </p>
            </button>
            <button
              onClick={() => setNavMode("command")}
              className={cn(
                "text-left rounded-lg border p-3 transition-all",
                navMode === "command"
                  ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                  : "border-border hover:border-foreground/30"
              )}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold">Command Rail</span>
                {navMode === "command" && <Badge variant="secondary" className="text-[10px]">Active</Badge>}
              </div>
              <p className="text-xs text-muted-foreground">
                Curated rail emphasizing Command Center & Jobs, with a Quick Action launcher and "More" for the long tail.
              </p>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Visual Language — Classic (shadcn baseline) vs Koredor (custom).
          Saved instantly. Classic remains the safe fallback. Koredor
          applies five anti-AI decisions globally: grouped-inset cards
          on tinted canvas, hairlines instead of shadows, real type
          scale (28/17/15/13), 2px left-edge status accent, and zero
          gradients. */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Visual Language</CardTitle>
          <CardDescription className="text-xs">
            Beta — applied across the whole app. <strong>Saved instantly</strong>. Classic is the safe shadcn baseline; Koredor is the custom Koredor look.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <button
              disabled
              className="text-left rounded-lg border p-3 opacity-40 cursor-not-allowed border-border"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold">Classic</span>
                <Badge variant="outline" className="text-[10px]">Disabled</Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Safe fallback. Standard shadcn cards, shadows, and borders. No surfaces altered.
              </p>
            </button>
            <button
              onClick={() => {
                setSkin("cockpit");
                toast.success("Koredor · Beta active", {
                  description: "Custom look applied across the app. Switch back to Classic any time.",
                });
              }}
              className={cn(
                "text-left rounded-lg border p-3 transition-all",
                skin === "cockpit"
                  ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                  : "border-border hover:border-foreground/30"
              )}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold">Koredor · Beta</span>
                {skin === "cockpit" && <Badge variant="secondary" className="text-[10px]">Active</Badge>}
              </div>
              <p className="text-xs text-muted-foreground">
                Custom look. Inset cards on tinted canvas, hairlines instead of shadows, 2px status rail, real type scale.
              </p>
            </button>
          </div>

          {/* Live preview — only adopts Koredor when that skin is active.
              When Classic is selected, renders as a stock shadcn card so the
              difference is unmistakable. */}
          {skin === "cockpit" ? (
            <div
              className="cockpit-surface rounded-lg overflow-hidden p-4"
              style={{ background: "hsl(210 22% 96%)" }}
            >
              <div className="bg-card p-4 space-y-3 rounded-lg" style={{ border: "1px solid hsl(210 16% 90%)", borderLeft: "2px solid hsl(207 80% 48%)" }}>
                <div className="space-y-1">
                  <p className="cockpit-caption">JOB-2841 · Tampa</p>
                  <h3 className="cockpit-title">Smith bathroom remodel</h3>
                </div>
                <div className="cockpit-divider" />
                <div className="grid grid-cols-3 gap-4 cockpit-body">
                  <div><p className="cockpit-caption">Materials</p><p className="font-semibold">8 of 10</p></div>
                  <div><p className="cockpit-caption">Permit</p><p className="font-semibold">Approved</p></div>
                  <div><p className="cockpit-caption">Install</p><p className="font-semibold">Apr 24</p></div>
                </div>
              </div>
              <p className="cockpit-caption mt-3 text-center">Koredor — inset card on tinted canvas, 2px left rail, no shadow</p>
            </div>
          ) : (
            <div className="rounded-lg border bg-card shadow-sm overflow-hidden p-4 space-y-3">
              <div className="space-y-1">
                <h3 className="flex items-center gap-2 text-base font-semibold leading-none tracking-tight">
                  <span className="inline-block h-2 w-2 rounded-full bg-primary" /> Smith Bathroom Remodel
                </h3>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="font-mono text-[11px]">JOB-2841</span>
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Tampa</Badge>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-xs">
                <div><p className="text-muted-foreground">Materials</p><p className="font-medium">8 of 10</p></div>
                <div><p className="text-muted-foreground">Permit</p><p className="font-medium">Approved</p></div>
                <div><p className="text-muted-foreground">Install</p><p className="font-medium">Apr 24</p></div>
              </div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground text-center pt-1">Classic — stock shadcn card with shadow + border</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Custom Shortcut Groups */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Shortcut Groups</CardTitle>
              <CardDescription>Custom groups that reference existing modules — no duplication.</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowGroupDialog(true)}>
              <FolderPlus className="h-3.5 w-3.5 mr-1.5" />
              Create Group
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {customGroups.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No custom groups. Create one to pin frequently-used modules together.
            </p>
          ) : (
            <div className="space-y-2">
              {customGroups.map((group) => (
                <div key={group.id} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <div className="flex items-center gap-3">
                    <Link2 className="h-4 w-4 text-primary" />
                    <div>
                      <p className="text-sm font-medium">{group.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {group.itemPaths.length} shortcut{group.itemPaths.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeGroup(group.id)}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Category List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Navigation Categories</CardTitle>
          <CardDescription>Reorder and toggle visibility of sidebar categories and their items.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-1">
          {categories.map((cat, catIdx) => (
            <div key={cat.id} className={cn("rounded-lg border transition-colors", !cat.visible && "opacity-50 bg-muted/30")}>
              {/* Category Header */}
              <div className="flex items-center gap-2 px-3 py-2.5">
                <GripVertical className="h-4 w-4 text-muted-foreground/40 shrink-0" />
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveCategory(catIdx, -1)} disabled={catIdx === 0}>
                    <ChevronUp className="h-3 w-3" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveCategory(catIdx, 1)} disabled={catIdx === categories.length - 1}>
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </div>
                <span className="text-sm font-semibold flex-1">{cat.label}</span>
                <Badge variant="outline" className="text-[10px]">
                  {cat.items.filter((i) => i.visible).length}/{cat.items.length}
                </Badge>
                <button
                  onClick={() => toggleCategoryVisibility(catIdx)}
                  className="p-1 rounded hover:bg-muted transition-colors"
                >
                  {cat.visible ? (
                    <Eye className="h-4 w-4 text-primary" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
              </div>

              {/* Sub-items */}
              {cat.visible && (
                <div className="px-3 pb-2 space-y-0.5 ml-8 border-t border-border/40 pt-1.5">
                  {cat.items.map((item, itemIdx) => (
                    <div
                      key={item.path}
                      className={cn(
                        "flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors",
                        !item.visible && "opacity-40"
                      )}
                    >
                      <div className="flex items-center gap-0.5">
                        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => moveItem(catIdx, itemIdx, -1)} disabled={itemIdx === 0}>
                          <ChevronUp className="h-2.5 w-2.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => moveItem(catIdx, itemIdx, 1)} disabled={itemIdx === cat.items.length - 1}>
                          <ChevronDown className="h-2.5 w-2.5" />
                        </Button>
                      </div>
                      <span className="flex-1 font-medium">{item.label}</span>
                      <span className="text-[10px] text-muted-foreground font-mono">{item.path}</span>
                      <Switch
                        checked={item.visible}
                        onCheckedChange={() => toggleItemVisibility(catIdx, itemIdx)}
                        className="scale-75"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Phase-ready note */}
      <Card className="border-muted">
        <CardContent className="py-3 px-4 flex items-start gap-3">
          <Shield className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
          <div className="text-xs text-muted-foreground">
            <strong className="text-foreground">Role-based layouts (Phase Ready).</strong> The data model supports
            per-role navigation configs. This will be activated in a future release to allow different
            sidebar layouts per role.
          </div>
        </CardContent>
      </Card>

      {/* Create Shortcut Group Dialog */}
      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Shortcut Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Group Name</label>
              <Input
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="e.g., My Quick Links"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Select Modules</label>
              <div className="mt-2 max-h-60 overflow-y-auto space-y-0.5 border rounded-md p-2">
                {allModules.map((mod) => (
                  <label key={mod.path} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted/50 cursor-pointer text-sm">
                    <Checkbox
                      checked={selectedPaths.includes(mod.path)}
                      onCheckedChange={(checked) => {
                        setSelectedPaths((prev) =>
                          checked ? [...prev, mod.path] : prev.filter((p) => p !== mod.path)
                        );
                      }}
                    />
                    <span className="flex-1">{mod.label}</span>
                    <span className="text-[10px] text-muted-foreground">{mod.category}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGroupDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateGroup} disabled={!newGroupName.trim() || selectedPaths.length === 0}>
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
